import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, share, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiListResponse, Download, ReportPagedData } from '../models/general.models';
import { Project } from '../models/project.module';
import {
  ExposureHistogramData, ExposureMetadataKey, ExposureMetadataKeysTyped,
  LayerData,
  ReportAggregationBody,
  ReportCreate,
  RiskReport,
  RiskReportCollection,
  RiskReportCollectionLineGeometry
} from '../models/report.model';
import { DownloadService } from './download.service';
import { UserService } from './user.service';
import { Filter } from '../pages/report/AOISReport/AOISReport.component';
import { QueryConfig } from '../models/filter.models';
import { FilterUtils } from '../utils/filter-utils';
import { QueryBuilderService } from './query-builder.service';

@Injectable({
  providedIn: 'root'
})
export class NewReportService {
  private baseUrl: string = environment.reportBaseUrl;
  private tilesUrl: string = environment.tilesBaseUrl;
  private readonly sharedRequests = {
    reports: {},
    details: {}
  };

  public breadCrumbs: BehaviorSubject<string[]> = new BehaviorSubject([]);
  public detailsLayerData: Subject<LayerData[]> = new Subject();
  public selectedLayer: Subject<string> = new Subject();
  public detailsType: Subject<string> = new Subject();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private downloadService: DownloadService,
    private queryBuilder: QueryBuilderService
  ) {
  }

  public createReport(
    body: ReportCreate
  ): Observable<RiskReport> {
    const endpoint = `${this.baseUrl}/reports`;
    return this.http.post<RiskReport>(endpoint, body);
  }

  public getReports(config?: {
    [key: string]: any;
  }): Observable<ReportPagedData<RiskReport>> {
    let endpoint = `${this.baseUrl}/reports`;
    if (config && Object.entries(config).length) {
      Object.entries(config).forEach((entry, index) => {
        let jointSymbol = '&';
        if (index == 0) {
          jointSymbol = '?';
        }
        endpoint += `${jointSymbol}${entry[0]}=${entry[1]}`;
      });
    }
    return this.http.get<ReportPagedData<RiskReport>>(endpoint);
  }

  public getReport(id: string): Observable<RiskReport> {
    const endpoint = `${this.baseUrl}/reports/${id}`;
    return this.http.get<RiskReport>(endpoint);
  }

  public getMetadataKeysProperties(
    reportId: string,
    collectionId: string
  ): Observable<string[]> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/metadata-keys/properties`;
    return this.http.get<{ metadata_keys: string[] }>(endpoint).pipe(map((res) => res.metadata_keys));
  }

  public getMetadataKeys(
    reportId: string,
    collectionId: string
  ): Observable<ExposureMetadataKey[]> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/metadata-keys`;
    return this.http.get<ExposureMetadataKeysTyped>(endpoint).pipe(map((res) => res.metadata_keys));
  }

  public getReportCollectionGeometryLayerData(reportId: string, collectionId: string, geometryId: string): Observable<ApiListResponse<any>> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/geometries/${geometryId}/layer-data`;
    return this.http.get<ApiListResponse<any>>(endpoint);
  }

  public getHistogram(reportId: string, collectionId: string): Observable<ApiListResponse<any>> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/exposure-histogram`;
    return this.http.get<ApiListResponse<any>>(endpoint);
  }

  public getMetadataValues(
    reportId: string,
    collectionId: string,
    key: string,
    search?: string
  ): Observable<Array<string>> {
    let endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/metadata-values/${key}`;
    if (search) {
      endpoint += `?search=${search}`;
    }

    return this.http.get<Array<string>>(endpoint);
  }

  public getExposureHistogram(
    reportId: string,
    collectionId: string
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/exposure-histogram`;
    return this.http.get<any>(endpoint);
  }

  public getMetadataKeysByPath(
    reportId: string,
    collectionId: string,
    path: string
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/metadata-keys/${path}`;
    return this.http.get<any>(endpoint);
  }

  public getReportDetails(
    reportId: string,
    collectionId: string,
    id: string
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}/geometries/${id}`;
    if (!this.sharedRequests.details[id]) {
      this.sharedRequests.details[id] = this.http.get<any>(endpoint).pipe(
        catchError((err) => {
          delete this.sharedRequests.details[id];
          return throwError(() => err);
        }),
        share()
      );
    }
    return this.sharedRequests.details[id];
  }

  public updateReport(
    id: string,
    body: Partial<RiskReport>
  ): Observable<RiskReport> {
    const endpoint = `${this.baseUrl}/reports/${id}`;
    return this.http.put<RiskReport>(endpoint, body);
  }

  public getReportScore(
    id: string,
    body: ReportAggregationBody
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/reports/${id}/collections/aggregate`;
    return this.http.post<any>(endpoint, body);
  }

  public getReportCollectionScore(
    id: string,
    collectionId: string
  ): Observable<ExposureHistogramData> {
    const endpoint = `${this.baseUrl}/reports/${id}/collections/${collectionId}/exposure-histogram`;
    return this.http.get<ExposureHistogramData>(endpoint);
  }

  public getReportScoreByType(
    id: string,
    type: string,
    chips: Array<Filter>
  ): Observable<ExposureHistogramData> {
    const endpoint = `${this.baseUrl}/reports/${id}/exposure-histogram-by-type?geometry_type=${type}`;
    const query: QueryConfig = {
      filters: undefined
    };
    if (chips.length) {
      chips.forEach((filter) => {
        if (filter.type === '__range') {
          query.filters = FilterUtils.numberRange(filter.key.name, filter.value, filter.value2);
        }
      });
    }
    return this.queryBuilder.get<ExposureHistogramData>(endpoint, query);
  }

  public getReportPdf(id: string, filename: string): Observable<Download> {
    const endpoint = `${this.baseUrl}/downloads/report/${id}`;
    return this.downloadService.download(endpoint, `${filename}.pdf`);
  }

  public getReportXLSX(
    id: string,
    filename: string,
    filterStr?: string
  ): Observable<Download> {
    const endpoint = `${this.baseUrl}/downloads/report/${id}?format=xlsx${
      filterStr ? `&${filterStr}` : ''
    }`;
    return this.downloadService.download(endpoint, `${filename}.xlsx`);
  }

  public getAreaReportPdf(
    id: string,
    areaId: number | string,
    filename: string
  ): Observable<Download> {
    const endpoint = `${this.baseUrl}/downloads/report/${id}/collection/${areaId}`;
    return this.downloadService.download(endpoint, `${filename}.pdf`);
  }

  public getAreaReportXLSX(
    id: string,
    areaId: number | string,
    filename: string,
    urlFilter?
  ): Observable<Download> {
    const endpoint = `${
      this.baseUrl
    }/downloads/report/${id}/collection/${areaId}?format=xlsx${
      urlFilter ? `&${urlFilter}` : ''
    }`;
    return this.downloadService.download(endpoint, `${filename}.xlsx`);
  }

  public getReportAnalyse(
    id: string,
    config?: {
      [key: string]: any;
    },
    filter?: string
  ): Observable<ApiListResponse<RiskReportCollection>> {
    let endpoint = `${this.baseUrl}/reports/${id}/collections`;
    if (config && Object.entries(config).length) {
      Object.entries(config).forEach((entry, index) => {
        let jointSymbol = '&';
        if (index == 0) {
          jointSymbol = '?';
        }
        endpoint += `${jointSymbol}${entry[0]}=${entry[1]}`;
      });
    }
    if (filter) {
      endpoint += `&${filter}`;
    }
    return this.http.get<ApiListResponse<RiskReportCollection>>(endpoint);
  }

  public getReportAnalyseById(
    reportId: string,
    collectionId: string,
    config?: {
      [key: string]: any;
    }
  ): Observable<RiskReportCollection> {
    let endpoint = `${this.baseUrl}/reports/${reportId}/collections/${collectionId}`;
    if (config && Object.entries(config).length) {
      Object.entries(config).forEach((entry, index) => {
        let jointSymbol = '&';
        if (index == 0) {
          jointSymbol = '?';
        }
        endpoint += `${jointSymbol}${entry[0]}=${entry[1]}`;
      });
    }
    return this.http.get<RiskReportCollection>(endpoint);
  }

  public getReportCollectionGeometries(id: string, collectionId: string): Observable<ApiListResponse<RiskReportCollectionLineGeometry>> {
    const endpoint = `${this.baseUrl}/reports/${id}/collections/${collectionId}/geometries`;
    return this.http.get<ApiListResponse<RiskReportCollectionLineGeometry>>(endpoint);
  }

  public deleteReport(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/reports/${id}`;
    return this.http.delete<any>(endpoint);
  }

  public shareReport(
    body: { user_id: string | number },
    id: string
  ): Observable<RiskReport> {
    const endpoint = `${this.baseUrl}/shares`;
    const shareData = {
      resource_type: 'report',
      resource_id: id,
      shared_with_type: 'user',
      shared_with_id: body.user_id,
      permission: 'read'
    };
    return this.http.post<RiskReport>(endpoint, shareData);
  }

  public leaveSharedProject(): Observable<Project> {
    // Note: This functionality might need to be implemented differently in the new API
    // For now, we'll return an empty observable
    console.warn(
      'leaveSharedProject functionality may need to be reimplemented for the new API'
    );
    return new Observable<Project>((observer) => {
      observer.complete();
    });
  }

  public removeShareReport(): Observable<RiskReport> {
    // Note: This functionality might need to be implemented differently in the new API
    // For now, we'll return an empty observable
    console.warn(
      'removeShareReport functionality may need to be reimplemented for the new API'
    );
    return new Observable<RiskReport>((observer) => {
      observer.complete();
    });
  }

  public getAreaQuickReportLayerInfo(
    layer_file: string,
    bbox: number[],
    bins: string,
    categorical: boolean
  ): Observable<any> {
    let endpoint = `${
      this.tilesUrl
    }/cog/statistics?url=file://${layer_file}&bbox=${bbox.join(
      ','
    )}&crs=3857&buffer=4000`;
    if (categorical) {
      endpoint += `&categorical=${categorical}`;
    } else {
      endpoint += `&histogram_bins=${bins}`;
    }
    return this.http.get(endpoint);
  }

  public getAreaQuickReportLayerImages(
    layer_file: string,
    bbox: number[],
    query: string
  ): Observable<any> {
    const endpoint = `${this.tilesUrl}/cog/bbox/${bbox.join(
      ','
    )}/400x400.png?url=file://${layer_file}${query}`;
    return this.http.get(endpoint, { responseType: 'blob' });
  }

  public getAreaQuickReportLayerPointInfo(
    layer_file: string,
    coordinates: number[]
  ): Observable<any> {
    const endpoint = `${this.tilesUrl}/cog/point/${coordinates[0].toFixed(
      6
    )},${coordinates[1].toFixed(6)}?url=file://${layer_file}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.userService.currentAccessToken}`
    });
    return this.http.get(endpoint, { headers });
  }

  public getAggregationsFields(): Observable<ExposureMetadataKeysTyped> {
    const endpoint = `${this.baseUrl}/aggregation-configurations`;
    return this.http.get<ExposureMetadataKeysTyped>(endpoint);
  }

  public createAggregationConfig(
    body: { [key: string]: any }
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/aggregation-configurations`;
    return this.http.post<any>(endpoint, body);
  }

  public getAggregationConfigs(): Observable<any> {
    const endpoint = `${this.baseUrl}/aggregation-configurations`;
    return this.http.get<any>(endpoint);
  }

  public deleteAggregationConfigs(
    config_id: string
  ): Observable<any> {
    const endpoint = `${this.baseUrl}/aggregation-configurations/${config_id}`;
    return this.http.delete<any>(endpoint);
  }

  public calculateAllAggregations(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.complete();
    });
  }

  public gerReportAggregations(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/reports/${id}/collections/aggregate`);
  }
}
